/**
 * Live console layer — binds the existing pages/components to the real
 * management API. Design rules:
 * - The server is the authority: 401s gate the UI, 403 step-up requirements
 *   and reason codes surface verbatim; the console never fakes success.
 * - Routes without a live P1 surface render an explicit "not available"
 *   panel instead of fixture data.
 * - The bearer token lives in memory + sessionStorage (cleared on logout /
 *   tab close); it is the operator-issued credential from bootstrap.
 */

import React, { useCallback, useEffect, useState } from "react";
import { ApiError, ManagementApi } from "./api.js";
import { CredentialsPage } from "./pages/index.jsx";
import { BillingPage } from "./pages/Billing.jsx";

export const consoleApi = new ManagementApi();

const TOKEN_KEY = "zerofare.console.token";

/** Token store: memory + sessionStorage, subscribable for refetch triggers. */
class TokenStore {
  #listeners = new Set<() => void>();
  #token: string | null = null;

  constructor() {
    try {
      this.#token = sessionStorage.getItem(TOKEN_KEY);
    } catch {
      this.#token = null; // storage unavailable (hardened browser, tests)
    }
    if (this.#token !== null) consoleApi.setToken(this.#token);
  }

  get(): string | null {
    return this.#token;
  }

  set(token: string): void {
    this.#token = token;
    consoleApi.setToken(token);
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* in-memory only */
    }
    for (const listener of this.#listeners) listener();
  }

  clear(): void {
    this.#token = null;
    consoleApi.setToken("");
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* in-memory only */
    }
    for (const listener of this.#listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}

export const tokenStore = new TokenStore();

function useToken(): string | null {
  const [token, setToken] = useState<string | null>(() => tokenStore.get());
  useEffect(() => tokenStore.subscribe(() => setToken(tokenStore.get())), []);
  return token;
}

type ResourceState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

/** Fetches a management resource; refetches when the token changes. */
function useApiResource<T>(path: string): { state: ResourceState<T>; refetch: () => void } {
  const token = useToken();
  const [state, setState] = useState<ResourceState<T>>({ status: "loading" });
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (token === null) {
      setState({ status: "unauthorized" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    consoleApi
      .request<T>({ method: "GET", path })
      .then((envelope) => {
        if (!cancelled) setState({ status: "ready", data: envelope.data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) setState({ status: "unauthorized" });
        else
          setState({
            status: "error",
            message: error instanceof Error ? error.message : "request failed",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [path, token, tick]);
  return { state, refetch };
}

function idempotencyKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

/** Login gate: paste the operator-issued credential; nothing else renders until present. */
export function LoginGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const token = useToken();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  if (token !== null) return <>{children}</>;
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const candidate = value.trim();
    if (candidate === "") return;
    setChecking(true);
    setError(null);
    try {
      await consoleApi.request({ method: "GET", path: "/v1/healthz" });
      tokenStore.set(candidate);
    } catch (verifyError) {
      // healthz is unauthenticated; verify the token against a real route.
      try {
        await consoleApi.request({ method: "GET", path: "/v1/api-keys" });
        tokenStore.set(candidate);
      } catch (authError) {
        setError(
          authError instanceof ApiError && authError.status === 401
            ? "Credential rejected by the server (401)."
            : verifyError instanceof Error
              ? verifyError.message
              : "Sign-in failed.",
        );
      }
    } finally {
      setChecking(false);
    }
  };
  return (
    <div className="page" style={{ maxWidth: 420, margin: "8vh auto" }}>
      <h1>ZeroFare Console</h1>
      <p className="muted">Sign in with the management credential issued by your operator.</p>
      <form onSubmit={(event) => void submit(event)}>
        <label htmlFor="zf-token">Management credential</label>
        <input
          id="zf-token"
          type="password"
          autoComplete="off"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="zf_…"
        />
        {error !== null ? <p className="error">{error}</p> : null}
        <p>
          <button className="primary" type="submit" disabled={checking}>
            {checking ? "Checking…" : "Sign in"}
          </button>
        </p>
      </form>
    </div>
  );
}

function SignOutButton(): React.ReactElement {
  return (
    <p>
      <button type="button" onClick={() => tokenStore.clear()}>
        Sign out
      </button>
    </p>
  );
}

function ResourceError({ message }: { message: string }): React.ReactElement {
  return <p className="error">The server reported an error: {message}</p>;
}

function Loading(): React.ReactElement {
  return <p className="muted">Loading…</p>;
}

function Unavailable({ page }: { page: string }): React.ReactElement {
  return (
    <div className="page">
      <h1>{page}</h1>
      <div className="banner">
        This surface is not available in the current deployment. The fail-closed default keeps it
        disabled until the corresponding service activation gate passes — the console will never
        show placeholder data here.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live pages
// ---------------------------------------------------------------------------

interface ApiKeyView {
  readonly id: string;
  readonly label: string;
  readonly scopes: readonly string[];
  readonly createdAt: number;
  readonly revokedAt: number | null;
  readonly expiresAt: number | null;
}

export function LiveCredentials(): React.ReactElement {
  const { state, refetch } = useApiResource<{ data: ApiKeyView[] }>("/v1/api-keys");
  const [created, setCreated] = useState<string | null>(null);
  const [label, setLabel] = useState("console-key");
  const [actionError, setActionError] = useState<string | null>(null);
  const create = async (): Promise<void> => {
    setActionError(null);
    try {
      const envelope = await consoleApi.request<{ id: string; plaintext: string }>({
        method: "POST",
        path: "/v1/credentials",
        body: { label, scopes: ["management"] },
        idempotencyKey: idempotencyKey(),
      });
      setCreated(envelope.data.plaintext);
      refetch();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "create failed");
    }
  };
  const revoke = async (id: string): Promise<void> => {
    setActionError(null);
    try {
      await consoleApi.request({
        method: "DELETE",
        path: `/v1/credentials/${encodeURIComponent(id)}`,
      });
      refetch();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "revoke failed");
    }
  };
  return (
    <div className="page page-credentials">
      <h1>Credentials</h1>
      <SignOutButton />
      {state.status === "loading" ? <Loading /> : null}
      {state.status === "unauthorized" ? (
        <p className="error">Session expired — sign in again.</p>
      ) : null}
      {state.status === "error" ? <ResourceError message={state.message} /> : null}
      {state.status === "ready" ? (
        <>
          {created !== null ? (
            <div className="banner">
              <strong>Save this secret now — it is shown exactly once.</strong>
              <br />
              <span className="secret">{created}</span>
            </div>
          ) : null}
          {actionError !== null ? <p className="error">{actionError}</p> : null}
          <div className="panel">
            <label htmlFor="zf-new-label">New credential label</label>
            <input
              id="zf-new-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <p>
              <button className="primary" type="button" onClick={() => void create()}>
                Create credential
              </button>
            </p>
          </div>
          <CredentialsPage
            keys={state.data.data.map((k) => ({
              id: k.id,
              label: k.label,
              revokedAt: k.revokedAt,
            }))}
          />
          <table>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Scopes</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.data.data.map((k) => (
                <tr key={k.id}>
                  <td>
                    <code>{k.id}</code>
                  </td>
                  <td>{k.scopes.join(", ")}</td>
                  <td>{k.revokedAt === null ? <span className="ok">active</span> : "revoked"}</td>
                  <td>
                    {k.revokedAt === null ? (
                      <button className="danger" type="button" onClick={() => void revoke(k.id)}>
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">
            Revocation requires fresh step-up on the server; a 403 reason code is surfaced verbatim
            if required.
          </p>
        </>
      ) : null}
    </div>
  );
}

interface DecisionView {
  readonly id: string;
  readonly state: string;
  readonly reasonCode: string;
  readonly subject: string;
  readonly subjectId: string;
  readonly decidedAt: number;
}

export function LiveDecisions(): React.ReactElement {
  const { state } = useApiResource<{ data: DecisionView[] }>("/v1/decisions");
  return (
    <div className="page page-decisions">
      <h1>Decisions</h1>
      {state.status === "loading" ? <Loading /> : null}
      {state.status === "unauthorized" ? (
        <p className="error">Session expired — sign in again.</p>
      ) : null}
      {state.status === "error" ? <ResourceError message={state.message} /> : null}
      {state.status === "ready" ? (
        <table>
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Subject</th>
              <th scope="col">State</th>
              <th scope="col">Reason</th>
              <th scope="col">Decided</th>
            </tr>
          </thead>
          <tbody>
            {state.data.data.map((d) => (
              <tr key={d.id}>
                <td>
                  <code>{d.id}</code>
                </td>
                <td>{d.subject}</td>
                <td>{d.state}</td>
                <td>{d.reasonCode}</td>
                <td>{new Date(d.decidedAt).toISOString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

interface SecurityEventView {
  readonly id: string;
  readonly kind: string;
  readonly detail: string;
  readonly recordedAt: number;
}

export function LiveSecurity(): React.ReactElement {
  const { state } = useApiResource<{ data: SecurityEventView[] }>("/v1/security-events");
  return (
    <div className="page page-security">
      <h1>Security</h1>
      {state.status === "loading" ? <Loading /> : null}
      {state.status === "unauthorized" ? (
        <p className="error">Session expired — sign in again.</p>
      ) : null}
      {state.status === "error" ? <ResourceError message={state.message} /> : null}
      {state.status === "ready" ? (
        state.data.data.length === 0 ? (
          <p className="muted">No security events recorded for this tenant yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th scope="col">Kind</th>
                <th scope="col">Detail</th>
                <th scope="col">Recorded</th>
              </tr>
            </thead>
            <tbody>
              {state.data.data.map((e, i) => (
                <tr key={`${e.id}-${i}`}>
                  <td>{e.kind}</td>
                  <td>{e.detail}</td>
                  <td>{new Date(e.recordedAt).toISOString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </div>
  );
}

interface SubscriptionView {
  readonly id?: string;
  readonly planId?: string;
  readonly state: string;
}

export function LiveBilling(): React.ReactElement {
  const { state } = useApiResource<SubscriptionView>("/v1/subscriptions");
  return (
    <div className="page page-billing">
      <h1>Billing</h1>
      {state.status === "loading" ? <Loading /> : null}
      {state.status === "unauthorized" ? (
        <p className="error">Session expired — sign in again.</p>
      ) : null}
      {state.status === "error" ? <ResourceError message={state.message} /> : null}
      {state.status === "ready" ? (
        <BillingPage
          fixture={{
            subscriptionFeeMicros: 0,
            planName: state.data.planId ?? "none",
            providerChargeMicros: 0,
            sponsoredCapacityMicros: 0,
            state: state.data.state === "active" ? "active" : "none",
            paidThrough: 0,
          }}
        />
      ) : null}
      <p className="muted">
        Payment processing is an external gate and remains disabled; the plan and subscription state
        above come from the live service.
      </p>
    </div>
  );
}

interface JobView {
  readonly id: string;
  readonly kind: string;
  readonly state: string;
  readonly attempts: number;
}

export function LiveJobs(): React.ReactElement {
  const { state, refetch } = useApiResource<{ data: JobView[] }>("/v1/jobs");
  const [actionError, setActionError] = useState<string | null>(null);
  const enqueue = async (): Promise<void> => {
    setActionError(null);
    try {
      await consoleApi.request({
        method: "POST",
        path: "/v1/jobs",
        body: { kind: "purge", tenantId: null, payload: "{}" },
        idempotencyKey: idempotencyKey(),
      });
      refetch();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "enqueue failed");
    }
  };
  return (
    <div className="page page-cache">
      <h1>Jobs</h1>
      {state.status === "loading" ? <Loading /> : null}
      {state.status === "unauthorized" ? (
        <p className="error">Session expired — sign in again.</p>
      ) : null}
      {state.status === "error" ? <ResourceError message={state.message} /> : null}
      {state.status === "ready" ? (
        <>
          {actionError !== null ? <p className="error">{actionError}</p> : null}
          <p>
            <button type="button" onClick={() => void enqueue()}>
              Enqueue purge job
            </button>
          </p>
          <table>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Kind</th>
                <th scope="col">State</th>
                <th scope="col">Attempts</th>
              </tr>
            </thead>
            <tbody>
              {state.data.data.map((j) => (
                <tr key={j.id}>
                  <td>
                    <code>{j.id}</code>
                  </td>
                  <td>{j.kind}</td>
                  <td>{j.state}</td>
                  <td>{j.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}

/** Route table for main.tsx: page id -> live renderer or explicit unavailability. */
export const LIVE_PAGES: Readonly<Record<string, () => React.ReactElement>> = Object.freeze({
  setup: () => (
    <div className="page page-setup">
      <h1>Setup</h1>
      <div className="banner">
        Your operator provisions tenants and credentials with
        <code> pnpm bootstrap:tenant</code>. Sign in with the management credential it printed once;
        API keys for the gateway are issued the same way or from the Credentials page.
      </div>
    </div>
  ),
  routes: () => <Unavailable page="Routes" />,
  policies: () => <Unavailable page="Policies" />,
  decisions: () => <LiveDecisions />,
  quota: () => <Unavailable page="Quota" />,
  cache: () => <LiveJobs />,
  privacy: () => <Unavailable page="Privacy" />,
  credentials: () => <LiveCredentials />,
  billing: () => <LiveBilling />,
  security: () => <LiveSecurity />,
  evals: () => <Unavailable page="Evals" />,
});
